import { ChangeEvent, FormEvent, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGroupSelectionContext } from '@context/groupSelectionContext';
import { useToastContext } from '@context/toastContext';
import { getFormDataObj, isErrorResponse, safelyParseJson } from '@utils/utils';

type UseSaveRouteAsSelectionSetOptions = {
    ids: string[];
};

export const useSaveRouteAsSelectionSet = ({ ids }: UseSaveRouteAsSelectionSetOptions) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [setName, setSetName] = useState('');
    const [saving, setSaving] = useState(false);
    const { createGroupSelectionSet } = useGroupSelectionContext();
    const { t } = useTranslation();
    const { addToast } = useToastContext();

    const setModalOpen = useCallback((visible: boolean) => {
        if (visible) {
            setSetName('');
        }
        setIsModalOpen(visible);
    }, []);

    const openModal = useCallback(() => {
        setModalOpen(true);
    }, [setModalOpen]);

    const closeModal = useCallback(() => {
        setModalOpen(false);
    }, [setModalOpen]);

    const onNameChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setSetName(event.target.value);
    }, []);

    const canSave = useMemo(
        () => ids.length > 0 && setName.trim().length > 0 && !saving,
        [ids, setName, saving]
    );

    const onSubmit = useCallback(
        async (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (!ids.length) {
                addToast(t('road-network.group-selection-found-no-features'), 'warning');
                return;
            }

            const formDataEntryObj = getFormDataObj(event);
            const name = ((formDataEntryObj['name'] as string) ?? '').trim();

            if (!name) {
                addToast(t('common.not-all-required-fields-are-filled'), 'warning');
                return;
            }

            setSaving(true);
            const resp = await createGroupSelectionSet(name, ids);
            if (isErrorResponse(resp)) {
                try {
                    const parsedErr =
                        typeof resp.error === 'string'
                            ? safelyParseJson(resp.error)?.error
                            : resp.error.error;
                    addToast(parsedErr, 'error');
                } catch (e) {
                    console.log('Replan. Error parsing error response', resp, e);
                }
            } else {
                addToast(t('common.save-successful'), 'success');
                setIsModalOpen(false);
            }
            setSaving(false);
        },
        [ids, createGroupSelectionSet, addToast, t]
    );

    return {
        isModalOpen,
        openModal,
        closeModal,
        setModalOpen,
        setName,
        onNameChange,
        onSubmit,
        canSave,
        saving,
    };
};
